
import { computed, defineComponent, watch } from 'vue';
import Modal from '@/components/Modal.vue';
import { useBatch, useClaims, useTaxonomy, useToast } from '@/hooks';
import { throttle } from 'lodash';
import { Filter, FILTER_KEY } from '@/hooks/useFilter';
import { injectOrThrow } from '@/utils';

export default defineComponent({
  name: 'ModalCloseBatch',
  components: {
    Modal,
  },
  props: {
    batchCode: {
      type: String,
      required: true,
    },
    supplierId: {
      type: Number,
      required: true,
    },
  },
  emits: ['close', 'complete'],
  setup(props, { emit }) {
    /**
     * Filter state; provided in Home.vue
     */
    const _filter: Filter = injectOrThrow(FILTER_KEY);

    const { isClosingBatch, closeBatchSuccess, closeBatch } = useBatch(
      undefined,
      undefined,
      props.batchCode
    );
    const { getClaimsStatusByFilter } = useClaims();
    const { toggleToast } = useToast();
    const { getSupplierName } = useTaxonomy();

    const supplierName = getSupplierName(props.supplierId);

    /**
     * Lock the UI while the API is submitting,
     * and keep it locked once the API returns successfully, until the Modal closes itself
     */
    const isUiLocked = computed(() => {
      return isClosingBatch.value || closeBatchSuccess.value === true;
    });

    const closeModal = () => {
      emit('close');
    };

    const _closeBatch = throttle(() => closeBatch(), 100);

    const onCloseBatch = () => {
      if (!!props.supplierId && !!props.batchCode && !isUiLocked.value) {
        _closeBatch();
      }
    };

    watch(closeBatchSuccess, () => {
      if (closeBatchSuccess.value) {
        getClaimsStatusByFilter(_filter.toLocationQuery());
        toggleToast(
          true,
          'success',
          `Successfully closed batch ${props.batchCode} for ${supplierName}`
        );
        closeModal();
        emit('complete');
      }
    });

    return {
      closeModal,
      isClosingBatch,
      isUiLocked,
      onCloseBatch,
      supplierName,
    };
  },
});
